import { type ClassificationNodeV2, ClassificationsProviderV2 } from '@/providers/classification/classifications-v2.provider';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe, NgStyle } from '@angular/common';
import { Component, Input, type OnInit, inject } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { type Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { type ClassificationNodeId, ClassificationNodeType } from '../../interfaces/Classification.interface';
import { ClassificationNodeNamePipe } from '../../pipes/classification-node-name.pipe';
import { BuildingsProvider } from '../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../providers/dashboarding.provider';
import { ProjectsProvider } from '../../providers/projects/projects.provider';

@Component({
    styleUrls: ['./node-picture.scss'],
    selector: 'node-picture',
    templateUrl: 'node-picture.html',
    imports: [NgStyle, SvgIconComponent, AsyncPipe, ClassificationNodeNamePipe]
})
export class NodePictureComponent implements OnInit {
    private _dashboardingProvider = inject(DashboardingProvider);
    private _classificationsProvider = inject(ClassificationsProviderV2);
    private _buildingsProvider = inject(BuildingsProvider);
    private _projectsProvider = inject(ProjectsProvider);

    currentNodeId$!: Observable<ClassificationNodeId>;
    pictureToDisplay$!: Observable<string>;
    location$!: Observable<string>;
    isBuildingNode$!: Observable<boolean>;

    @Input() bannerTitle!: string;
    @Input() bannerColor!: string;
    @Input() bannerIcon!: string;
    @Input() textColor!: string;

    ngOnInit() {
        this.currentNodeId$ = this._dashboardingProvider.getSelectedNodeId$();

        const currentNode$ = this.currentNodeId$.pipe(
            switchMap(nodeId => this._classificationsProvider.getClassificationNodeById$(nodeId)),
            filter(isNotNullish)
        );

        this.isBuildingNode$ = currentNode$.pipe(
            map(currentNode => currentNode.classificationNodeType === ClassificationNodeType.BUILDING)
        );

        this.pictureToDisplay$ = currentNode$.pipe(
            switchMap(currentNode => {
                return this.getPictureUrl$(currentNode);
            })
        );

        this.location$ = currentNode$.pipe(
            switchMap(currentNode => {
                if (currentNode.classificationNodeType !== ClassificationNodeType.BUILDING) return of(null);
                return this._buildingsProvider.getBuildingDetails$(currentNode.classificationNodeId);
            }),
            map(building => (building ? building.location.address : null!))
        );
    }

    private getPictureUrl$(currentNode: ClassificationNodeV2): Observable<string> {
        const defaultProjectPicture = 'assets/images/default_project.jpg';
        if (currentNode && currentNode.classificationNodeType === ClassificationNodeType.BUILDING) {
            return this._buildingsProvider.getBuildingPhotoUrl$(currentNode.classificationNodeId);
        } else {
            return this._projectsProvider.getUserCurrentProject$().pipe(
                map(project => {
                    if (!project) return defaultProjectPicture;
                    if (project.bigPictureUrl) return project.bigPictureUrl;
                    if (project.logoUrl) return project.logoUrl;
                    return defaultProjectPicture;
                })
            );
        }
    }
}
